// MR-MOB — **THE MEMBER DROPDOWN IS PORTALLED TO `<body>`, SO NO ROOM'S CONTENT CAN EVER SIT ABOVE
// IT.** (her order, 2026-08-10, with her screenshot as evidence)
//
// *"On phone, the Member dropdown opens BEHIND the Magic Room's content, the room's elements render
// on top of it, this room only… Fix the proven way: portal the dropdown to body with the token
// z-index family so no room's content can ever sit above it."*
//
// **THE CAUSE, MEASURED AT COORDINATES BEFORE A LINE WAS WRITTEN (R25), AND IT IS ONE CAUSE FOR BOTH
// HALVES OF HER REPORT.** M5b moved the Magic Room's two edge controls (Notepaper, How this room
// works) to the TOP-RIGHT of the scene on phones and gave them `z-index: 60`. The site nav is
// `z-index: 20`. At 393x852 those controls paint at `224..383 x 8..78` and the Member pill sits at
// `268..375 x 23..61` — **entirely inside them** — so the room really does render on top of the
// header, and the dropdown that opens out of that header goes under the same lid. It is this room
// only because that rule names `.mr-edge-controls` and nothing else.
//
// **SO THE ROOM'S OWN Z-INDEX IS NOT WHAT IS FIXED HERE, AND THAT IS THE POINT OF HER "PROVEN WAY".**
// Bumping the nav above 60 wins today and loses to the next room that needs a high layer; the
// dropdown belongs OUT of the header's stacking context entirely, on the token every other
// full-screen panel in this house already uses (`--z-header-panel`). A room cannot outrank something
// that is not in its tree.
//
// **THE ANCHOR IS RE-READ ON SCROLL AND RESIZE, AND THAT IS NOT A FLOURISH.** A portalled panel is
// `position: fixed` in the viewport, so it no longer travels with the pill the way an absolutely
// positioned child did. Without the re-read, scrolling the page underneath an open menu leaves it
// stranded at coordinates its button has left.
//
// `anchorBox` is PURE AND EXPORTED so a FAST gate RUNS it — including the two cases a browser makes
// awkward to stage: a null anchor, and a pill so close to the right edge that the panel would hang
// off it.
import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { anchorBox } from "../accountMenu.js";

export type AnchorBox = { top: number; right: number };

export function AccountMenuPortal({ anchorEl, children }: { anchorEl: HTMLElement | null; children: ReactNode }) {
  const [box, setBox] = useState<AnchorBox>(() => anchorBox(
    anchorEl ? anchorEl.getBoundingClientRect() : null,
    typeof window === "undefined" ? 0 : window.innerWidth,
  ));

  useEffect(() => {
    const sync = () => setBox(anchorBox(
      anchorEl ? anchorEl.getBoundingClientRect() : null,
      window.innerWidth,
    ));
    sync();
    // `true` for the capture phase, so an inner scroller moving the pill is heard too — the header
    // is not always the thing that scrolls (NAV-1's finding: the document is not always what moves).
    window.addEventListener("scroll", sync, true);
    window.addEventListener("resize", sync);
    return () => {
      window.removeEventListener("scroll", sync, true);
      window.removeEventListener("resize", sync);
    };
  }, [anchorEl]);

  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="account-menu__portal" style={{ top: `${box.top}px`, right: `${box.right}px` }}>
      {children}
    </div>,
    document.body,
  );
}
